import { classNames } from '@commenda/utils';
import { Column, Row, Table } from '@tanstack/react-table';
import {
  CELL_HORIZONTAL_PADDING,
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_MIN_COLUMN_WIDTH,
  FIT_CONTENT_MAX_SLACK,
  FIT_CONTENT_SAMPLE_SIZE,
} from './constants';
import { ColumnDefWithMeta, ColumnGroup, CustomColumnDef } from './types';

/** Maps a column `align` to a flex `justify-*` class. */
export const getAlignClass = (align?: 'left' | 'center' | 'right'): string =>
  align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : '';

let measureCanvas: HTMLCanvasElement | null = null;

// Cells with any of these are CSS-sized, not text — measure them via the DOM path.
const WIDGET_CONTENT_SELECTOR =
  'button, svg, input, select, textarea, img, [role="button"], [role="switch"]';

const MEASURING_STYLE_ID = 'kakashi-datatable-measuring';
// Out-of-flow max-content snapshot: true intrinsic width without reflowing the live
// table; descendant resets undo truncation/flex-shrink so nothing collapses.
const MEASURING_CSS = `
[data-dt-measuring]{position:absolute!important;left:-99999px!important;top:0!important;width:max-content!important;max-width:none!important;white-space:nowrap!important;}
[data-dt-measuring] *{white-space:nowrap!important;overflow:visible!important;text-overflow:clip!important;max-width:none!important;flex-shrink:0!important;}
`;
let measuringStyleInjected = false;
const ensureMeasuringStyle = () => {
  if (measuringStyleInjected || typeof document === 'undefined') return;
  if (!document.getElementById(MEASURING_STYLE_ID)) {
    const el = document.createElement('style');
    el.id = MEASURING_STYLE_ID;
    el.textContent = MEASURING_CSS;
    document.head.appendChild(el);
  }
  measuringStyleInjected = true;
};

const escapeColumnId = (id: string): string =>
  window.CSS?.escape ? window.CSS.escape(id) : id;

// Text path: widest textContent via canvas — no layout reads, and textContent (not
// scrollWidth) means the cap can't ratchet wider as the column grows.
const measureTextWidth = (cells: HTMLElement[]): number => {
  measureCanvas ??= document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return 0;
  // Uniform font down the column — read once (cells[0] is the bold header).
  const { fontWeight, fontSize, fontFamily } = getComputedStyle(cells[0]);
  ctx.font = `${fontWeight} ${fontSize} ${fontFamily}`;
  let max = 0;
  for (const cell of cells) {
    const text = cell.textContent?.trim();
    if (text) max = Math.max(max, ctx.measureText(text).width);
  }
  return max;
};

// Up to N cells that actually contain widget content (row-order, early-exit).
// Sampling real widget cells — not a blind first-N slice — keeps empty/atypical
// leading rows from skewing the cap, and bounds how many cells go out-of-flow.
const collectWidgetSample = (cells: HTMLElement[]): HTMLElement[] => {
  const sample: HTMLElement[] = [];
  for (const cell of cells) {
    if (cell.querySelector(WIDGET_CONTENT_SELECTOR)) {
      sample.push(cell);
      if (sample.length >= FIT_CONTENT_SAMPLE_SIZE) break;
    }
  }
  return sample;
};

// Widget path: batched write → batched read (one reflow) → restore. Out-of-flow
// and synchronous, so the visible table never shifts.
const measureIntrinsicWidth = (cells: HTMLElement[]): number => {
  ensureMeasuringStyle();
  for (const cell of cells) cell.setAttribute('data-dt-measuring', '');
  try {
    let max = 0;
    for (const cell of cells) {
      max = Math.max(max, cell.getBoundingClientRect().width);
    }
    return max;
  } finally {
    for (const cell of cells) cell.removeAttribute('data-dt-measuring');
  }
};

/**
 * Resize cap for a fit-content column (widest cell + padding + slack), measured once
 * at drag-start; null ⇒ uncapped. Text columns measure every cell via canvas (no
 * reflow); widget columns sample a few cells and measure intrinsic width via the DOM.
 * Best-effort: any DOM failure degrades to null rather than breaking the resize.
 */
export const measureColumnContentCap = (
  tableEl: HTMLElement | null,
  columnId: string
): number | null => {
  if (!tableEl || typeof document === 'undefined') return null;
  try {
    const cells = Array.from(
      tableEl.querySelectorAll<HTMLElement>(
        `[data-dt-col="${escapeColumnId(columnId)}"]`
      )
    );
    if (!cells.length) return null;

    // Widget columns measure a bounded sample (reflow cost); text columns measure
    // every cell via canvas (cheap, no reflow).
    const widgetSample = collectWidgetSample(cells);
    const contentWidth = widgetSample.length
      ? measureIntrinsicWidth(widgetSample)
      : measureTextWidth(cells);

    if (contentWidth <= 0) return null;
    return Math.ceil(
      contentWidth + CELL_HORIZONTAL_PADDING + FIT_CONTENT_MAX_SLACK
    );
  } catch {
    return null;
  }
};

// Trailing flex spacer (resize path): the table's only auto-width track, so it
// absorbs leftover width and collapses to 0 on overflow. Not a real column.
export const FLEX_SPACER_STYLE = { width: 'auto' } as const;
export const FLEX_SPACER_BODY_CLASSNAME = 'p-0';
export const FLEX_SPACER_HEADER_CLASSNAME = 'bg-bg-primary p-0';

export const getColumnWidthStyle = (
  columnWidth?: number,
  isResizeEnabled?: boolean,
  resizeSize?: number,
  columnId?: string,
  // false ⇒ omit maxWidth so the browser can grow this column (non-sticky cells).
  lockMaxWidth: boolean = true
) => {
  if (isResizeEnabled && resizeSize != null) {
    // Width off a CSS var so a resize updates one variable instead of re-rendering
    // every cell (fallback covers first paint). No min floor — it would force
    // narrow columns wider and desync sticky offsets.
    const v = columnId
      ? `var(--col-${columnId}-size, ${resizeSize}px)`
      : `${resizeSize}px`;
    return lockMaxWidth
      ? { width: v, minWidth: v, maxWidth: v }
      : { width: v, minWidth: v };
  }
  if (columnWidth) {
    // If width is explicitly specified by user, use it as fixed width
    // Ensure min-width matches width for sticky columns to maintain positioning
    return lockMaxWidth
      ? {
          width: `${columnWidth}px`,
          minWidth: `${columnWidth}px`,
          maxWidth: `${columnWidth}px`,
        }
      : {
          width: `${columnWidth}px`,
          minWidth: `${columnWidth}px`,
        };
  }
  // If width is not specified, allow auto-sizing with minimum width
  // For sticky columns, min-width is critical - it ensures the column never shrinks below this
  // Don't set maxWidth so content can expand as needed
  // Note: table cells respect min-width automatically with table-layout
  return {
    width: 'auto',
    minWidth: `${DEFAULT_COLUMN_WIDTH}px`,
  };
};

export const getColumnId = <TData, TValue>(
  column: CustomColumnDef<TData, TValue>
): string => {
  // Prefer explicit id so header/cell lookup matches TanStack Table's column.id
  if (column.id) {
    return column.id;
  }
  if ('accessorKey' in column) {
    return column.accessorKey as string;
  }
  return '';
};

const getFlatColumnIndex = <TData, TValue>({
  groupIndex,
  columnIndex,
  groups,
}: {
  groupIndex: number;
  columnIndex: number;
  groups: ColumnGroup<TData, TValue>[];
}) => {
  let flatIndex = 0;
  for (let i = 0; i < groupIndex; i++) {
    flatIndex += groups[i].columns.length;
  }
  return flatIndex + columnIndex;
};

// Original width-based computation (fallback)
export const getStickyPosition = <TData, TValue>(
  groups: ColumnGroup<TData, TValue>[],
  activeGroup: ColumnGroup<TData, TValue>,
  columnIndex: number
): number => {
  if (activeGroup.sticky === 'left') {
    // Find index of current group
    const currentGroupIndex = groups.findIndex(
      (g: ColumnGroup<TData, TValue>) => g.id === activeGroup.id
    );

    // Sum widths of all columns in previous left-sticky groups
    const previousGroupsWidth = groups
      .slice(0, currentGroupIndex)
      .filter((g: ColumnGroup<TData, TValue>) => g.sticky === 'left')
      .reduce(
        (sum: number, g: ColumnGroup<TData, TValue>) =>
          sum +
          g.columns.reduce(
            (colSum: number, col: CustomColumnDef<TData, TValue>) =>
              colSum + (col.width || DEFAULT_COLUMN_WIDTH),
            0
          ),
        0
      );

    // Add widths of columns before the current column in the same group
    const currentGroupOffset = activeGroup.columns
      .slice(0, columnIndex)
      .reduce((sum, col) => sum + (col.width || DEFAULT_COLUMN_WIDTH), 0);

    return previousGroupsWidth + currentGroupOffset;
  }

  if (activeGroup.sticky === 'right') {
    // Find index of current group
    const currentGroupIndex = groups.findIndex((g) => g.id === activeGroup.id);

    // Sum widths of all columns in later right-sticky groups
    const laterGroupsWidth = groups
      .slice(currentGroupIndex + 1)
      .filter((g: ColumnGroup<TData, TValue>) => g.sticky === 'right')
      .reduce(
        (sum: number, g: ColumnGroup<TData, TValue>) =>
          sum +
          g.columns.reduce(
            (colSum, col) => colSum + (col.width || DEFAULT_COLUMN_WIDTH),
            0
          ),
        0
      );

    // Add widths of columns after the current column in the same group
    const currentGroupOffset = activeGroup.columns
      .slice(columnIndex + 1)
      .reduce((sum, col) => sum + (col.width || DEFAULT_COLUMN_WIDTH), 0);

    return laterGroupsWidth + currentGroupOffset;
  }

  return 0;
};

const liveWidthOf = <TData, TValue>(
  table: Table<TData>,
  col: CustomColumnDef<TData, TValue>
): number => {
  const id = getColumnId(col);
  const columnApi = table.getColumn(id);

  const fallback = col.width || DEFAULT_COLUMN_WIDTH;

  if (!columnApi) return fallback;

  // Prefer the live size (reflects drags); using the static `col.width` left
  // sticky offsets stale after a resize.
  const measuredSize = columnApi.getSize?.() as number | undefined;
  if (
    measuredSize !== undefined &&
    measuredSize > 0 &&
    !Number.isNaN(measuredSize)
  ) {
    return measuredSize;
  }

  const columnWithHeaderSize = columnApi as Column<TData> & {
    getHeaderSize?: () => number;
  };
  const headerSize = columnWithHeaderSize.getHeaderSize?.();
  if (headerSize && headerSize > 0) return headerSize;

  return fallback;
};

const isColumnVisibleInTable = <TData, TValue>(
  table: Table<TData>,
  col: CustomColumnDef<TData, TValue>
): boolean => {
  const id = getColumnId(col);
  return table.getColumn(id)?.getIsVisible?.() ?? true;
};

// Visible columns whose widths precede a sticky cell's offset (left-sticky: all
// sticky columns to its left; right-sticky: all to its right). The sticky
// offset is the sum of their widths.
const collectStickyOffsetColumns = <TData, TValue>(
  table: Table<TData>,
  groups: ColumnGroup<TData, TValue>[],
  activeGroup: ColumnGroup<TData, TValue>,
  columnIndex: number
): CustomColumnDef<TData, TValue>[] => {
  const isColumnVisible = (col: CustomColumnDef<TData, TValue>) =>
    isColumnVisibleInTable(table, col);
  const currentColumn = activeGroup.columns[columnIndex];
  if (!currentColumn || !isColumnVisible(currentColumn)) return [];

  const currentColumnId = getColumnId(currentColumn);
  const currentGroupIndex = groups.findIndex((g) => g.id === activeGroup.id);
  const visibleInGroup = activeGroup.columns.filter(isColumnVisible);
  const out: CustomColumnDef<TData, TValue>[] = [];

  if (activeGroup.sticky === 'left') {
    groups
      .slice(0, currentGroupIndex)
      .filter((g) => g.sticky === 'left')
      .forEach((g) => out.push(...g.columns.filter(isColumnVisible)));
    for (const col of visibleInGroup) {
      if (getColumnId(col) === currentColumnId) break;
      out.push(col);
    }
  } else if (activeGroup.sticky === 'right') {
    groups
      .slice(currentGroupIndex + 1)
      .filter((g) => g.sticky === 'right')
      .forEach((g) => out.push(...g.columns.filter(isColumnVisible)));
    let found = false;
    for (const col of visibleInGroup) {
      if (getColumnId(col) === currentColumnId) {
        found = true;
        continue;
      }
      if (found) out.push(col);
    }
  }
  return out;
};

// Sticky offset in px from live column sizes (non-resize / fallback path).
export const getStickyPositionLive = <TData, TValue>(
  table: Table<TData>,
  groups: ColumnGroup<TData, TValue>[],
  activeGroup: ColumnGroup<TData, TValue>,
  columnIndex: number
): number =>
  collectStickyOffsetColumns(table, groups, activeGroup, columnIndex).reduce(
    (sum, col) => sum + liveWidthOf(table, col),
    0
  );

// Sticky offset as a calc() of the same `--col-<id>-size` vars cells use, so the
// offset tracks a resize via CSS even while the body is frozen (memoized).
export const getStickyPositionVar = <TData, TValue>(
  table: Table<TData>,
  groups: ColumnGroup<TData, TValue>[],
  activeGroup: ColumnGroup<TData, TValue>,
  columnIndex: number
): string => {
  const cols = collectStickyOffsetColumns(table, groups, activeGroup, columnIndex);
  if (!cols.length) return '0px';
  return `calc(${cols.map((c) => `var(--col-${getColumnId(c)}-size)`).join(' + ')})`;
};

const getStickySideTotalWidth = <TData, TValue>(
  table: Table<TData>,
  groups: ColumnGroup<TData, TValue>[],
  side: 'left' | 'right'
): number => {
  let total = 0;
  for (const group of groups) {
    if (group.sticky !== side) continue;
    for (const col of group.columns) {
      if (!isColumnVisibleInTable(table, col)) continue;
      total += liveWidthOf(table, col);
    }
  }
  return total;
};

export const getLeftStickyTotalWidth = <TData, TValue>(
  table: Table<TData>,
  groups: ColumnGroup<TData, TValue>[]
): number => getStickySideTotalWidth(table, groups, 'left');

export const getRightStickyTotalWidth = <TData, TValue>(
  table: Table<TData>,
  groups: ColumnGroup<TData, TValue>[]
): number => getStickySideTotalWidth(table, groups, 'right');

export function getStickyColumnClasses<TData, TValue>(
  group: ColumnGroup<TData, TValue>,
  config?: { isHeader: boolean; bgClassName?: string }
): string {
  const isHeader = !!config?.isHeader;

  const isSticky = group.sticky === 'left' || group.sticky === 'right';
  if (!isSticky) return '';

  // Sticky cells need an opaque background or scrolled content shows through.
  // Caller can override the default via bgClassName (e.g. row-level tint that
  // would otherwise be masked by the sticky cell's own bg).
  // bgClassName replaces the default outright, hover included, so a table that suppresses row hover
  // does not get it back here. Callers that want the hover must include it themselves.
  const cellBg = config?.bgClassName ?? 'bg-bg-primary group-hover:bg-bg-primary-hover';

  return classNames(
    'sticky',
    // Headers stay on bg-bg-primary; the override only applies to body cells.
    isHeader ? 'z-30 bg-bg-primary' : `z-20 ${cellBg}`
  );
}

export const shouldRenderCell = <TData, TValue>({
  row,
  columnIndex,
  groupIndex,
  groups,
  table,
}: {
  row: Row<TData>;
  columnIndex: number;
  groupIndex: number;
  groups: ColumnGroup<TData, TValue>[];
  table: Table<TData>;
}) => {
  const flatCurrentIndex = getFlatColumnIndex({
    groupIndex,
    columnIndex,
    groups,
  });

  // Check all previous columns in all groups for spans that might affect this cell
  for (let gi = 0; gi <= groupIndex; gi++) {
    const currentGroup = groups[gi];
    const startColIndex = gi === groupIndex ? 0 : 0;

    for (
      let ci = startColIndex;
      ci < (gi === groupIndex ? columnIndex : currentGroup.columns.length);
      ci++
    ) {
      const prevColumn = currentGroup.columns[ci];
      const prevColumnId = getColumnId(prevColumn);

      // Only consider spans if the column is visible
      if (
        prevColumn &&
        prevColumn.getSpan &&
        table.getColumn(prevColumnId)?.getIsVisible()
      ) {
        const span = prevColumn.getSpan(row.original);
        if (span) {
          const flatStartIndex = getFlatColumnIndex({
            groupIndex: gi,
            columnIndex: ci,
            groups,
          });
          const spanEndIndex = flatStartIndex + span.colSpan - 1;

          // If this cell is within a span range of a visible column, don't render it
          if (flatCurrentIndex <= spanEndIndex) {
            return false;
          }
        }
      }
    }
  }
  return true;
};

// Function to calculate spanned width across groups
export const getSpannedWidth = <TData, TValue>({
  column,
  columnIndex,
  groupIndex,
  row,
  groups,
}: {
  column: CustomColumnDef<TData, TValue>;
  columnIndex: number;
  groupIndex: number;
  row: Row<TData>;
  groups: ColumnGroup<TData, TValue>[];
}) => {
  if (!column.getSpan) return column.width || 0;

  const span = column.getSpan(row.original);
  if (!span) return column.width || 0;

  let totalWidth = 0;
  let remainingSpan = span.colSpan;
  let currentGroupIndex = groupIndex;
  let currentColumnIndex = columnIndex;

  while (remainingSpan > 0 && currentGroupIndex < groups.length) {
    const currentGroup = groups[currentGroupIndex];
    const columnsInCurrentGroup =
      currentGroup.columns.length - currentColumnIndex;
    const columnsToInclude = Math.min(remainingSpan, columnsInCurrentGroup);

    totalWidth += currentGroup.columns
      .slice(currentColumnIndex, currentColumnIndex + columnsToInclude)
      .reduce((sum, col) => sum + (col.width || 0), 0);

    remainingSpan -= columnsToInclude;
    currentGroupIndex++;
    currentColumnIndex = 0;
  }

  return totalWidth;
};

export const groupDataByKey = <TData>(
  data: TData[],
  getGroupKey: (row: TData) => string
): Map<string, TData[]> => {
  const grouped = new Map<string, TData[]>();
  data.forEach((row) => {
    const key = getGroupKey(row);
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(row);
  });
  return grouped;
};

export const createColumnDef = <TData, TValue>(
  column: CustomColumnDef<TData, TValue>,
  groupId: string
): ColumnDefWithMeta<TData, TValue> => {
  const { meta, ...rest } = column;
  const sort = 'sort' in column ? column.sort : undefined;

  const enableSorting = !!sort?.shouldSort;
  const sortingFn = sort?.sortingFn
    ? (rowA: Row<TData>, rowB: Row<TData>, columnId: string) => {
        const valueA = rowA.getValue(columnId) as TValue;
        const valueB = rowB.getValue(columnId) as TValue;
        return sort.sortingFn?.(valueA, valueB) ?? 0;
      }
    : undefined;

  return {
    ...rest,
    size: column.width || DEFAULT_COLUMN_WIDTH,
    // Respect an explicitly-narrow width (e.g. a 60px action column); the
    // default min would force it wider since getSize() is floored to minSize.
    minSize:
      column.width != null && column.width > 0
        ? Math.min(column.width, DEFAULT_MIN_COLUMN_WIDTH)
        : DEFAULT_MIN_COLUMN_WIDTH,
    enableSorting,
    ...(sortingFn ? { sortingFn } : {}),
    meta: {
      ...(meta || {}),
      groupId,
      // expose UI nodes/icons in meta for consumers (header/global/grouped headers)
      sortUi: sort?.ui,
    },
  } as ColumnDefWithMeta<TData, TValue>;
};
